import {
  Component, Input, Output, EventEmitter,
  ElementRef, OnInit, HostListener, SimpleChanges, OnChanges
} from '@angular/core';
import "hammerjs";

enum SliderHandlerEnum {
  left = 0,
  right = 1
}

@Component({
  selector: 'range-slider',
  templateUrl: './range-slider.component.html',
  styleUrls: ['./range-slider.component.scss']
})
export class RangeSliderComponent implements OnInit, OnChanges {

  private sliderModel = [0, 0, 0];
  private step = 1;
  private totalDiff = 0;
  private startClientX = 0;
  private startPleft = 0;
  private startPRight = 0;
  private minValue: number;
  private maxValue: number;
  private minSelected: number;
  private maxSelected: number;
  private sliderInitiated = false;

  public sliderWidth = 0;
  public initValues: number[] = [];
  public currentValues: number[] = [0, 0];
  public handlerX: number[] = [0, 0];
  public isHandlerActive = false;
  public isTouchEventStart = false;
  public isMouseEventStart = false;
  public currentHandlerIndex = 0;
  public stepIndicatorPositions = [];
  public stepIndicatorLabel = [];
  public isDisabled = false;
  public hideTooltip = false;
  public hideValues = false;
  public customStepIndicator = false;

  public handlerIndex = SliderHandlerEnum;

  constructor(private el: ElementRef) {
  }

  @Input('min')
  set setMinValues(value: number) {
    if (!isNaN(value)) {
      this.minValue = Number(value);
    }
  }

  @Input('max')
  set setMaxValues(value: number) {
    if (!isNaN(value)) {
      this.maxValue = Number(value);
    }
  }

  @Input('minSelected')
  set setMinSelectedValues(value: number) {
    if (!isNaN(value) && this.minSelected !== Number(value)) {
      this.minSelected = Number(value);
    }
  }

  @Input('maxSelected')
  set setMaxSelectedValues(value: number) {
    if (!isNaN(value) && this.maxSelected !== Number(value)) {
      this.maxSelected = Number(value);
    }
  }
  @Input('step')
  set stepValue(value: number) {
    if (!isNaN(value)) {
      this.step = Number(value);
    }
  }
  @Input('stepIndicatorLabel')
  set setStepIndicatorLabel(value: Array<string>) {
    if (value) {
      this.stepIndicatorLabel = value;
      this.customStepIndicator = true;
    }
  }

  @Input() showStepIndicator = false;
  @Input() multiRange = true;
  @Input('hide-tooltip')
  set setHideTooltip(value: boolean) {
    this.hideTooltip = value;
  }
  @Input('hide-values')
  set setHideValues(value: boolean) {
    this.hideValues = value;
  }

  @Input('disabled')
  set setDisabled(value: boolean) {
    this.isDisabled = value;
  }

  @Input('sliderWidth')
  set setSliderWidth(value: number) {
    this.sliderWidth = value;
  }

  @Output() onChange = new EventEmitter<number[]>();

  ngOnInit() {
    this.initializeSlider();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.sliderInitiated) {
      if (!this.isNullOrEmpty(changes.setMinSelectedValues)
        && changes.setMinSelectedValues.previousValue === changes.setMinSelectedValues.currentValue) {
        return;
      }
      if (!this.isNullOrEmpty(changes.setMaxSelectedValues)
        && changes.setMaxSelectedValues.previousValue === changes.setMaxSelectedValues.currentValue) {
        return;
      }
      this.resetModel();
    }
  }

  public initializeSlider() {
    this.sliderWidth = this.sliderWidth || this.el.nativeElement.getElementsByClassName('bar')[0].offsetWidth;
    this.resetModel();
    this.sliderInitiated = true;
  }

  private resetModel() {
    this.validateSliderValues();
    this.sliderModel = [
      this.currentValues[0] - this.initValues[0],
      this.currentValues[1] - this.currentValues[0],
      this.initValues[1] - this.currentValues[1]
    ];

    this.totalDiff = this.sliderModel.reduce((prevValue, curValue) => prevValue + curValue, 0);

    if (this.totalDiff % this.step !== 0) {
      const newStep = this.findNextValidStepValue(this.totalDiff, this.step);
      this.step = newStep;
    }
    this.initializeStepIndicator();
    this.setHandlerPosition();
  }

  private validateSliderValues() {
    if (this.isNullOrEmpty(this.minValue) || this.isNullOrEmpty(this.maxValue)) {
      this.updateInitValues([0, 0]);
      this.updateCurrentValue([0, 0], true);
    } else if (this.minValue > this.maxValue) {
      this.updateInitValues([0, 0]);
      this.updateCurrentValue([0, 0], true);
    } else {
      this.initValues = [this.minValue, this.maxValue];
      if (this.isNullOrEmpty(this.minSelected) || this.minSelected < this.minValue || this.minSelected > this.maxValue) {
        this.minSelected = this.minValue;
      }
      if (this.isNullOrEmpty(this.maxSelected) || this.maxSelected < this.minValue || this.maxSelected > this.maxValue) {
        this.maxSelected = this.maxValue;
      }
      if (this.minSelected > this.maxSelected) {
        this.minSelected = this.minValue;
        this.maxSelected = this.maxValue;
      }
      this.updateCurrentValue([this.minSelected, this.maxSelected], true);
    }
  }

  private initializeStepIndicator() {
    if (this.showStepIndicator) {
      this.stepIndicatorPositions.length = 0;
      const numOfStepIndicators = this.totalDiff / this.step;
      if (this.sliderWidth / numOfStepIndicators >= 10) {
        const increment = this.sliderWidth / numOfStepIndicators;
        let leftPosition = increment;
        if (leftPosition) {
          this.stepIndicatorPositions.push(0);
          if (!this.customStepIndicator) {
            this.stepIndicatorLabel.push(this.minValue + '-' + (this.minValue + this.step - 1));
          }
        }
        let currPosition: number;
        while (this.stepIndicatorPositions.length < numOfStepIndicators + 1) {
          this.stepIndicatorPositions.push(+leftPosition.toFixed(2));
          leftPosition += increment;
          if (this.customStepIndicator) {
            currPosition = this.minValue + (this.stepIndicatorPositions.length * this.step);
            this.stepIndicatorLabel.push(currPosition + '-' + (currPosition + this.step - 1));
          }
        }
      }
    } else {
      this.stepIndicatorPositions.length = 0;
    }
  }

  private updateCurrentValue(arrayValue: number[], privateChange: boolean = false) {
    this.minSelected = this.currentValues[0] = arrayValue[0];
    this.maxSelected = this.currentValues[1] = arrayValue[1];
    if (!privateChange) {
      this.onChange.emit((this.multiRange) ? this.currentValues : [this.currentValues[0]]);
    }
  }

  private updateInitValues(arrayValue: number[]) {
    this.minValue = this.initValues[0] = arrayValue[0];
    this.maxValue = this.initValues[1] = arrayValue[1];
  }

  private setHandlerPosition() {
    let runningTotal = 0;
    this.updateCurrentValue([
      this.initValues[0] + this.sliderModel[0],
      this.initValues[1] - this.sliderModel[2]
    ]);
    for (let i = 0, len = this.sliderModel.length - 1; i < len; i++) {
      runningTotal += this.sliderModel[i];
      this.handlerX[i] = (runningTotal / this.totalDiff) * 100;
    }
  }

  private setModelValue(index: number, value: number) {
    if (this.step > 1) {
      value = Math.round(value / this.step) * this.step;
    }
    this.sliderModel[index] = value;
  }

  @HostListener('document:mouseup')
  @HostListener('document:panend')
  setHandlerActiveOff() {
    this.isMouseEventStart = false;
    this.isTouchEventStart = false;
    this.isHandlerActive = false;
  }

  public setHandlerActive(event: any, handlerIndex: number) {
    event.preventDefault();
    if (!this.isDisabled) {

      if (!this.isNullOrEmpty(event.clientX)) {
        this.startClientX = event.clientX;
        this.isMouseEventStart = true;
        this.isTouchEventStart = false;
      } else if (!this.isNullOrEmpty(event.deltaX)) {
        this.startClientX = event.deltaX;
        this.isTouchEventStart = true;
        this.isMouseEventStart = false;
      }
      if (this.isMouseEventStart || this.isTouchEventStart) {
        this.currentHandlerIndex = handlerIndex;
        this.startPleft = this.sliderModel[handlerIndex];
        this.startPRight = this.sliderModel[handlerIndex + 1];
        this.isHandlerActive = true;
      }
    }
  }

  public handlerSliding(event: any) {
    if ((this.isMouseEventStart && event.clientX) || (this.isTouchEventStart && event.deltaX)) {
      const movedX = Math.round(((event.clientX || event.deltaX) - this.startClientX) / this.sliderWidth * this.totalDiff);
      const nextPLeft = this.startPleft + movedX;
      const nextPRight = this.startPRight - movedX;
      if (nextPLeft >= 0 && nextPRight >= 0) {
        this.setModelValue(this.currentHandlerIndex, nextPLeft);
        this.setModelValue(this.currentHandlerIndex + 1, nextPRight);
        this.setHandlerPosition();
      }
    }
  }

  public isNullOrEmpty(obj: any): boolean {
    return obj === undefined || obj === null || obj === '';
  }

  public findNextValidStepValue(n: number, step: number): number {
    const divisorsSet1: number[] = [];
    const divisorsSet2: number[] = [];
    const sqrtNum = Math.sqrt(n);
    let newStep = -1;
    for (let i = 0; i < sqrtNum; i++) {
      if (n % i === 0) {
        if ((n / i) === i) {
          divisorsSet1.push(i);
        } else {
          divisorsSet1.push(i);
          divisorsSet2.push(n / i);
        }
      }
    }
    // Picking newStep by checking large set of divisors first
    for (let i = 0; i < divisorsSet2.length; i++) {
      if (step > divisorsSet2[i]) {
        newStep = divisorsSet2[i];
        break;
      }
    }
    if (newStep === -1) { // checking set of small divisors if newStep didn't find out till.
      for (let i = divisorsSet1.length - 1; i >= 0; i--) {
        if (step > divisorsSet1[i]) {
          newStep = divisorsSet1[i];
          break;
        }
      }
    }
    return (newStep === -1) ? 1 : newStep;
  }

}
